import { useNavigate } from 'react-router';
import { Play, Settings, IndianRupee, Users, AlertCircle, ChevronRight, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatCard } from '@/components/shared/StatCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/formatters';
import { usePlan } from '@/hooks/usePlan';
import { UpgradePrompt } from '@/components/shared/UpgradePrompt';
import { usePayrollRuns, usePayrollRun, useSalaryStructures, usePayrollSettings } from '@/api/queries/payroll.queries';
import { useEmployees } from '@/api/queries/employee.queries';

export default function PayrollPage() {
  const navigate = useNavigate();
  const { hasFeature } = usePlan();

  const { data: runsResp, isLoading: runsLoading } = usePayrollRuns();
  const { data: ssResp } = useSalaryStructures({ limit: 200 });
  const { data: empResp } = useEmployees({ status: 'ACTIVE', limit: 200 });
  const { data: settingsResp } = usePayrollSettings();

  const runs = runsResp?.data || [];
  const latestRun = runs[0];
  const { data: latestRunResp } = usePayrollRun(latestRun?.id);
  const items = latestRunResp?.data?.items || [];

  const structures = ssResp?.data || [];
  const employees = (empResp?.data || []).filter(e => e.status === 'ACTIVE' || !e.status);
  const settings = settingsResp?.data;

  const assignedIds = new Set(structures.map(s => s.employee_id));
  const unassignedCount = employees.filter(e => !assignedIds.has(e.id || e.employee_id)).length;
  const totalGross = structures.reduce((s, ss) => s + Number(ss.gross_monthly), 0);

  if (!hasFeature('payroll')) {
    return <UpgradePrompt feature="Payroll Processing" requiredPlan="Pro" description="Run monthly payroll with automatic PF, ESIC, TDS, and PT deductions on the Pro plan."/>;
  }

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Payroll"
        subtitle="Monthly salary processing with Indian statutory compliance"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" icon={Settings} onClick={() => navigate('/payroll/salary-structures')}>Salary Structures</Button>
            <Button size="sm" icon={Play} onClick={() => navigate('/payroll/run')}>Run Payroll</Button>
          </div>
        }
      />

      {/* Setup warnings */}
      {!settings?.bank_name && (
        <div className="flex items-center gap-3 p-3.5 rounded-[12px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => navigate('/settings/payroll-bank')}>
          <AlertCircle size={15} className="text-amber-600 shrink-0"/>
          <p className="text-[12px] text-amber-700 dark:text-amber-400 flex-1">Company bank details not configured — required for payslip generation. <b className="underline">Set up now →</b></p>
        </div>
      )}
      {unassignedCount > 0 && (
        <div className="flex items-center gap-3 p-3.5 rounded-[12px] bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => navigate('/payroll/salary-structures')}>
          <Users size={15} className="text-blue-600 shrink-0"/>
          <p className="text-[12px] text-blue-700 dark:text-blue-400 flex-1"><b>{unassignedCount} employee{unassignedCount>1?'s':''}</b> without salary structure — they won't be included in payroll. <b className="underline">Set up →</b></p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard title="Employees Configured" value={structures.length} subtitle={`${unassignedCount} pending`} color="blue"/>
        <StatCard title="Monthly Gross Liability" value={formatCurrency(totalGross)} color="purple"/>
        <StatCard title="PF Liability/Month" value={formatCurrency(structures.reduce((s, ss) => s + Math.min(Number(ss.basic), 15000) * 0.12 * 2, 0))} subtitle="Employee + Employer" color="orange"/>
        <StatCard title="Payroll Runs" value={runs.length} subtitle={latestRun ? `Last: ${latestRun.pay_period}` : 'None yet'} color="green"/>
      </div>

      {/* Latest run */}
      {latestRun && (
        <div className="card p-5 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/payroll/${latestRun.id}`)}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[16px] font-bold text-[var(--text-primary)]">{latestRun.pay_period}</h3>
              <p className="text-[12px] text-[var(--text-secondary)]">
                {latestRun.processed_on ? `Processed ${latestRun.processed_on.slice(0,10)}` : 'Processing'}
                {latestRun.paid_on ? ` · Paid ${latestRun.paid_on.slice(0,10)}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={latestRun.status}/>
              <ChevronRight size={16} className="text-[var(--text-tertiary)]"/>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
            {[
              ['Employees', latestRun.total_employees],
              ['Gross', formatCurrency(latestRun.total_gross)],
              ['PF (Emp)', formatCurrency(latestRun.total_pf_employee)],
              ['ESIC+PT', formatCurrency(Number(latestRun.total_esic_employee)+Number(latestRun.total_pt))],
              ['Net Paid', formatCurrency(latestRun.total_net)],
            ].map(([l, v]) => (
              <div key={l}>
                <p className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide">{l}</p>
                <p className="text-[15px] font-bold text-[var(--text-primary)]">{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Latest payslip items */}
      {items.length > 0 && (
        <div className="card p-0 overflow-x-auto">
          <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
            <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Payslips — {latestRun?.pay_period}</h3>
            <button onClick={() => navigate(`/payroll/${latestRun.id}`)} className="text-[12px] font-semibold text-[var(--accent)] hover:underline">View All →</button>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                {['Employee','Gross','PF','TDS','Net Pay','Status'].map(h=>(
                  <th key={h} className="text-left text-[11px] font-bold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {items.slice(0, 8).map(item => (
                <tr key={item.id} className="hover:bg-[var(--bg-muted)] transition-colors cursor-pointer" onClick={() => navigate(`/payroll/${latestRun.id}`)}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={item.employee_name} size="sm"/>
                      <div>
                        <p className="text-[13px] font-semibold text-[var(--text-primary)]">{item.employee_name}</p>
                        <p className="text-[11px] text-[var(--text-tertiary)]">{item.department}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[13px]">{formatCurrency(item.gross_salary)}</td>
                  <td className="px-5 py-3.5 text-[13px] text-red-500">−{formatCurrency(item.pf_employee)}</td>
                  <td className="px-5 py-3.5 text-[13px] text-red-500">−{formatCurrency(item.tds)}</td>
                  <td className="px-5 py-3.5 text-[13px] font-bold text-green-600">{formatCurrency(item.net_pay)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${item.payment_status==='PAID'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>
                      {item.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* No runs yet */}
      {!runsLoading && runs.length === 0 && (
        <div className="card py-16 text-center space-y-4">
          <IndianRupee size={40} className="mx-auto text-[var(--text-tertiary)]"/>
          <div>
            <p className="text-[16px] font-bold text-[var(--text-secondary)]">No payroll runs yet</p>
            <p className="text-[12px] text-[var(--text-tertiary)] mt-1">
              {structures.length === 0 ? 'First set up salary structures for your employees' : 'Ready to run your first payroll'}
            </p>
          </div>
          {structures.length === 0
            ? <Button onClick={() => navigate('/payroll/salary-structures')} icon={Settings}>Set Up Salaries</Button>
            : <Button onClick={() => navigate('/payroll/run')} icon={Play}>Run First Payroll</Button>}
        </div>
      )}

      {/* History */}
      {runs.length > 1 && (
        <div className="card p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--border)]">
            <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Payroll History</h3>
          </div>
          <div className="divide-y divide-[var(--border)]">
            {runs.slice(1).map(run => (
              <div key={run.id} onClick={() => navigate(`/payroll/${run.id}`)}
                className="flex items-center justify-between px-5 py-4 hover:bg-[var(--bg-muted)] cursor-pointer transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--bg-muted)] flex items-center justify-center">
                    <Clock size={14} className="text-[var(--text-tertiary)]"/>
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-[var(--text-primary)]">{run.pay_period}</p>
                    <p className="text-[11px] text-[var(--text-tertiary)]">{run.total_employees} employees · {run.period_start}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-[13px] font-bold text-[var(--text-primary)]">{formatCurrency(run.total_net)}</p>
                    <p className="text-[11px] text-[var(--text-tertiary)]">net paid</p>
                  </div>
                  <StatusBadge status={run.status}/>
                  <ChevronRight size={14} className="text-[var(--text-tertiary)]"/>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
